import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import { toast } from "react-toastify";
import i18next from "i18next";
import { queryClient } from "../../providers/QueryProvider";
import { queryKeys } from "../../constants/queryKeys";
import { User } from "../../components/pages/dashboard/users/UsersListComponent";
import { UniversalCacheManager } from "../../utils/cacheManager";
import { usersCache } from "../users";

export const groupsCache = new UniversalCacheManager();

const GROUP_TOAST = "dashboard.groupList.toast";

/** The server answers in English: the dashboard tells why a change of groups failed from its status and code. */
export const groupErrorKey = (
  err: AxiosError<{ code?: string }>,
  failed: string
): string => {
  if (err.response?.status === 402) return `${GROUP_TOAST}.subscription`;
  if (err.response?.status === 403) return `${GROUP_TOAST}.forbidden`;
  if (err.response?.data?.code === "RSUSKO036")
    return `${GROUP_TOAST}.notFound`;
  return `${GROUP_TOAST}.${failed}`;
};

const toastGroupError = (failed: string) => (err: AxiosError<any, any>) => {
  const key = groupErrorKey(err, failed);
  toast.error(i18next.t(key) as string, { toastId: key });
};

type GroupProps = {
  customer_identification: string;
  entityIdentification: string;
  groupId: string;
  pageParam?: number;
  limit?: number;
};

interface GroupUpdateProps extends GroupProps {
  data: any;
}

type CreateGroupProps = {
  customer_identification: string;
  entityIdentification: string;
  group_identification: string;
  data: any;
};

type Groups = {
  pageParam?: number;
  customer_identification: string;
  entityIdentification: string;
  limit: number;
  sortOrder?: "DESC" | "ASC" | "";
  enabled?: boolean;
};

type DeleteGroupsProps = {
  customer_identification: string;
  entityIdentification: string;
  groups: any;
};

type ExcludeUsersFromGroupProps = {
  customer_identification: string;
  entityIdentification: string;
  group_identification: string;
  users: any;
};

type AddCustomerToGroupProps = {
  customer_identification: string;
  entityIdentification: string;
  group_identification: string;
  customerId: string;
};

type ExcludeUserFromGroupProps = {
  customer_identification: string;
  entityIdentification: string;
  group_identification: string;
  customerId: any;
};

export const useDeleteGroup = () => {
  return useMutation<GroupProps, AxiosError<any, any>, any>(
    ({ customer_identification, entityIdentification, groupId }: GroupProps) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/group/${groupId}`
      ),
    {
      onSuccess: (res, variables) => {
        const groupCacheKey = `group_${variables.groupId}`;
        groupsCache.delete(groupCacheKey);

        const groupsListingPattern = `groups_${variables.entityIdentification}`;
        const groupUsersPattern = `group_users_${variables.groupId}`;
        groupsCache.clearByPattern(groupsListingPattern);
        groupsCache.clearByPattern(groupUsersPattern);

        queryClient.invalidateQueries([
          queryKeys.groups,
          queryKeys.group_users,
        ]);
      },
      onError: toastGroupError("deleteFailed"),
    }
  );
};

export const useUpdateGroup = () => {
  return useMutation<GroupUpdateProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      groupId,
      data,
    }: GroupUpdateProps) =>
      HttpService.patch(
        `/${customer_identification}/${entityIdentification}/group/${groupId}`,
        {
          group_name: data.group_name,
          group_description: data.group_description,
        }
      ),
    {
      onSuccess: (res, variables) => {
        const groupCacheKey = `group_${variables.groupId}`;
        groupsCache.delete(groupCacheKey);

        const groupsListingPattern = `groups_${variables.entityIdentification}`;
        groupsCache.clearByPattern(groupsListingPattern);

        queryClient.invalidateQueries([queryKeys.groups, queryKeys.group]);
      },
      onError: toastGroupError("updateFailed"),
    }
  );
};

export const useGetGroup = ({
  groupId,
  customer_identification,
  entityIdentification,
}: GroupProps) => {
  return useQuery<any, any, any>(
    queryKeys.group,
    async () => {
      const cacheKey = `group_${groupId}`;
      const cached = groupsCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/group/${groupId}`
      ).then((response) => {
        return response?.data?.element;
      });

      groupsCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: Boolean(
        groupId &&
          groupId.trim() &&
          customer_identification &&
          entityIdentification
      ),
      onError: toastGroupError("groupFailed"),
    }
  );
};

export const useGetGroupUsers = ({
  groupId,
  customer_identification,
  entityIdentification,
  pageParam,
  limit,
}: GroupProps) => {
  return useQuery<any, any, any>(
    [
      queryKeys.group_users,
      customer_identification,
      entityIdentification,
      groupId,
      limit,
      pageParam,
    ],
    async () => {
      const cacheKey = `group_users_${groupId}_${limit}_${pageParam}`;
      const cached = groupsCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/group/${groupId}/users?limit=${limit}&offset=${pageParam}`
      ).then((response) => {
        let result: Array<User> = [];
        response?.data?.element?.users?.map((item: any, index: number) => {
          result.push({
            id: index,
            user_identification: item?.user_identification,
            user_user_name: item?.user_user_name || null,
            user_last_name: item?.user_last_name || null,
            user_first_name: item?.user_first_name || null,
            user_birth_date: item?.user_birth_date || null,
            user_avatar: item?.user_avatar || null,
            user_phone: item?.user_phone || null,
            user_email: item?.user_email,
            user_country_code: item?.user_country_code || null,
            user_is_active: item?.user_is_active,
            user_role: item?.user_role,
            user_type: item?.user_type,
            user_account_status: item?.user_account_status,
            user_creation_date_time: item?.user_creation_date_time,
            user_last_connection: item?.user_last_connection || null,
          });
        });
        return {
          data: result || [],
          pagination: response?.data?.element?.pagination,
        };
      });

      groupsCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled: Boolean(
        groupId &&
          groupId.trim() &&
          customer_identification &&
          entityIdentification
      ),
      onError: toastGroupError("membersFailed"),
    }
  );
};

export const useGetGroups = ({
  limit,
  pageParam,
  customer_identification,
  entityIdentification,
  sortOrder,
  enabled = true,
}: Groups) => {
  return useQuery<any, any, any>(
    [
      queryKeys.groups,
      customer_identification,
      entityIdentification,
      limit,
      pageParam,
      sortOrder,
    ],
    async () => {
      const validSortOrder =
        sortOrder === "ASC" || sortOrder === "DESC" ? sortOrder : "DESC";

      const cacheKey = `groups_${entityIdentification}_${limit}_${pageParam}_${validSortOrder}`;
      const cached = groupsCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/group?limit=${limit}&page=${pageParam}&sortOrder=${validSortOrder}`
      ).then((response) => {
        return response?.data?.element;
      });

      groupsCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled,
      onError: toastGroupError("listFailed"),
    }
  );
};

export const useCreateroup = () => {
  return useMutation<CreateGroupProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      data,
    }: CreateGroupProps) =>
      HttpService.post(
        `${customer_identification}/${entityIdentification}/group`,
        data
      ),
    {
      onSuccess: (res, variables) => {
        toast.success(i18next.t(`${GROUP_TOAST}.created`) as string);
        const groupsListingPattern = `groups_${variables.entityIdentification}`;
        groupsCache.clearByPattern(groupsListingPattern);

        const countCacheKey = `entity_count_users_${variables.entityIdentification}`;
        groupsCache.delete(countCacheKey);

        queryClient.invalidateQueries([queryKeys.groups]);
        queryClient.invalidateQueries(["count-users"]);
      },
      onError: toastGroupError("createFailed"),
    }
  );
};

export const useDeleteGroups = () => {
  return useMutation<DeleteGroupsProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      groups,
    }: DeleteGroupsProps) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/group/bulk`,
        {
          data: { groups: groups },
        }
      ),
    {
      onSuccess: (res, variables) => {
        const groupsListingPattern = `groups_${variables.entityIdentification}`;
        groupsCache.clearByPattern(groupsListingPattern);
        groupsCache.clearByPattern("group_");
        groupsCache.clearByPattern("group_users_");

        queryClient.invalidateQueries([
          queryKeys.groups,
          queryKeys.group_users,
        ]);
      },
      onError: toastGroupError("deleteFailed"),
    }
  );
};

export const useDeleteUsersFromGroup = () => {
  return useMutation<ExcludeUsersFromGroupProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      group_identification,
      users,
    }: ExcludeUsersFromGroupProps) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/user/group/${group_identification}/bulk`,
        {
          data: { users: users },
        }
      ),
    {
      onSuccess: (res, variables) => {
        const groupUsersPattern = `group_users_${variables.group_identification}`;
        groupsCache.clearByPattern(groupUsersPattern);

        const user = `user_profile`;
        usersCache.clearByPattern(user);

        queryClient.invalidateQueries([
          queryKeys.groups,
          queryKeys.group_users,
          queryKeys.users,
        ]);
        queryClient.invalidateQueries(["entity_user"], { exact: false });
      },
      onError: toastGroupError("removeFailed"),
    }
  );
};

export const useAddUserToSpecifiedGroup = () => {
  return useMutation<AddCustomerToGroupProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      group_identification,
      customerId,
    }: AddCustomerToGroupProps) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/group/${group_identification}`,
        null
      ),
    {
      onSuccess: (res: any, variables) => {
        const cacheKey = `user_profile_${variables.customerId}`;
        usersCache.delete(cacheKey);

        const groupCacheKey = `group_users_${variables.group_identification}`;
        groupsCache.clearByPattern(groupCacheKey);

        queryClient.invalidateQueries(["entity_user", variables.customerId]);
        queryClient.invalidateQueries([queryKeys.group_users]);
      },
      onError: toastGroupError("addFailed"),
    }
  );
};

export const useDeleteUserFromGroup = () => {
  return useMutation<ExcludeUserFromGroupProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      group_identification,
      customerId,
    }: ExcludeUserFromGroupProps) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/group/${group_identification}`
      ),
    {
      onSuccess: (res, variables) => {
        const cacheKey = `user_profile_${variables.customerId}`;
        usersCache.delete(cacheKey);

        queryClient.invalidateQueries(["entity_user", variables.customerId]);

        const groupCacheKey = `group_users_${variables.group_identification}`;
        groupsCache.clearByPattern(groupCacheKey);

        queryClient.invalidateQueries([queryKeys.group_users]);
      },
      onError: toastGroupError("removeFailed"),
    }
  );
};

export const useAddUsersToSpecifiedGroup = () => {
  return useMutation<ExcludeUsersFromGroupProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      group_identification,
      users,
    }: ExcludeUsersFromGroupProps) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/group/${group_identification}/bulk`,
        {
          users: users,
        }
      ),
    {
      onSuccess: (res: any, variables) => {
        const groupUsersPattern = `group_users_${variables.group_identification}`;
        groupsCache.clearByPattern(groupUsersPattern);

        queryClient.invalidateQueries(["entity_user"], { exact: false });
        queryClient.invalidateQueries(queryKeys.group_users);
        queryClient.invalidateQueries(queryKeys.users);
        queryClient.invalidateQueries(queryKeys.searchUsers);
      },
      onError: toastGroupError("addFailed"),
    }
  );
};
